import classNames from 'classnames/bind';
import React from 'react';

import Button from '~/components/ui/Button';

import Markdown from '../Markdown';
import bannerData from './banner.json';
import styles from './index.scss';

const cx = classNames.bind(styles);

export function HomepageBanner({
  children,
  className,
  titleOnly,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  titleOnly?: boolean;
}) {
  return (
    <div
      className={cx(
        'HomepageBanner',
        { 'HomepageBanner--NoDescription': titleOnly },
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function HomepageBannerTitle({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('HomepageBanner__Title', className)} {...props}>
      {children}
    </div>
  );
}

export function HomepageBannerDescription({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('HomepageBanner__Description', className)} {...props}>
      {children}
    </div>
  );
}

export function HomepageBannerImage({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('HomepageBanner__Image', className)} {...props}>
      {children}
    </div>
  );
}

export function HomepageBannerCTA({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('HomepageBanner__CTA', className)} {...props}>
      {children}
    </div>
  );
}

interface BannerData {
  active: boolean;
  activeUntil: string | null;
  activeFrom: string | null;
  title: string;
  subtitle: string;
  buttonText: string;
  buttonLink: string;
  imageUrl: string;
  altText: string;
}

export default function Banner() {
  const data: BannerData = bannerData;

  const now = new Date();
  const isActive =
    data.active &&
    (!data.activeFrom || new Date(data.activeFrom) <= now) &&
    (!data.activeUntil || new Date(data.activeUntil) >= now);

  if (!isActive) {
    return null;
  }

  return (
    <HomepageBanner>
      {data.imageUrl && (
        <HomepageBannerImage>
          <img src={data.imageUrl} alt={data.altText} />
        </HomepageBannerImage>
      )}
      <div>
        <HomepageBannerTitle>{data.title}</HomepageBannerTitle>
        <HomepageBannerDescription>
          <Markdown>{data.subtitle}</Markdown>
        </HomepageBannerDescription>
        {data.buttonText && data.buttonLink && (
          <Button to={data.buttonLink}>{data.buttonText}</Button>
        )}
      </div>
    </HomepageBanner>
  );
}
